/**
 * Cross-source deduplication.
 *
 * The same hackathon legitimately appears on several platforms (an MLH member event
 * that also runs a Devpost submission page, for instance). A match requires ALL of:
 *
 *   1. similar names   — case-insensitive, punctuation stripped, Dice-coefficient ≥ 0.85
 *   2. overlapping dates
 *   3. the same city   — or both online
 *
 * Requiring all three keeps false positives down: "HackMIT" and "HackNYU" have
 * genuinely different names, and two unrelated "AI Hackathon"s in different cities
 * fail the city test even though their names collide.
 *
 * On a match we keep the record with the more complete data and merge the others'
 * links into a `sources` array, so the card can link back to every platform listing it.
 */

/** Lowercase, strip punctuation/accents, collapse whitespace. */
export function normalizeName(name) {
  return String(name || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * The key names are actually compared on.
 *
 * On top of normalizeName, this drops standalone 4-digit years. Platforms disagree
 * constantly about them — MLH lists "HackMIT" where Devpost lists "HackMIT 2026" —
 * and on bigram similarity that suffix alone scores only 0.75, under the threshold.
 *
 * Dropping the year is safe here specifically because a match ALSO requires
 * overlapping dates, so two different editions of the same annual event can never
 * collapse into one. Edition markers that aren't years ("Season 3", "2.0") are
 * deliberately left intact, since those do distinguish real events.
 */
export function matchKey(name) {
  return normalizeName(name)
    .replace(/\b(?:19|20)\d{2}\b/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Sørensen–Dice coefficient over character bigrams: 2·|A∩B| / (|A|+|B|).
 * Chosen over Levenshtein because it's order-tolerant, cheap, and handles the
 * real-world variations between platforms well.
 */
export function nameSimilarity(a, b) {
  const left = matchKey(a).replace(/\s/g, '');
  const right = matchKey(b).replace(/\s/g, '');

  if (!left || !right) return 0;
  if (left === right) return 1;
  if (left.length < 2 || right.length < 2) return left === right ? 1 : 0;

  const bigrams = new Map();
  for (let i = 0; i < left.length - 1; i += 1) {
    const gram = left.slice(i, i + 2);
    bigrams.set(gram, (bigrams.get(gram) || 0) + 1);
  }

  let intersection = 0;
  for (let i = 0; i < right.length - 1; i += 1) {
    const gram = right.slice(i, i + 2);
    const count = bigrams.get(gram) || 0;
    if (count > 0) {
      bigrams.set(gram, count - 1);
      intersection += 1;
    }
  }

  return (2 * intersection) / (left.length - 1 + (right.length - 1));
}

const DAY_MS = 24 * 60 * 60 * 1000;

/** Do the two events' date ranges overlap (with a day of slack for timezone skew)? */
function datesOverlap(a, b, slackDays = 1) {
  const aStart = Date.parse(a.start_date);
  const aEnd = Date.parse(a.end_date || a.start_date);
  const bStart = Date.parse(b.start_date);
  const bEnd = Date.parse(b.end_date || b.start_date);

  if ([aStart, aEnd, bStart, bEnd].some(Number.isNaN)) return false;

  const slack = slackDays * DAY_MS;
  return aStart - slack <= bEnd && bStart - slack <= aEnd;
}

/** Same city, or both online (no city on either side). */
function sameCity(a, b) {
  const aCity = normalizeName(a.venue_city);
  const bCity = normalizeName(b.venue_city);

  if (!aCity && !bCity) {
    // Neither has a city. Only treat as "same place" if the formats agree, so an
    // online event never merges with an in-person one whose venue failed to parse.
    return a.format === b.format;
  }
  if (!aCity || !bCity) return false;
  return aCity === bCity || nameSimilarity(aCity, bCity) >= 0.9;
}

const NAME_SIMILARITY_THRESHOLD = 0.85;

export function isDuplicate(a, b) {
  if (a.source_platform === b.source_platform) return false; // same platform => distinct events
  return (
    nameSimilarity(a.name, b.name) >= NAME_SIMILARITY_THRESHOLD &&
    datesOverlap(a, b) &&
    sameCity(a, b)
  );
}

/**
 * How much usable information a record carries. Coordinates and deadlines are weighted
 * heavily because they're what the filters actually run on.
 */
export function completenessScore(record) {
  let score = 0;
  if (record.lat != null && record.long != null) score += 4;
  if (record.registration_deadline) score += 3;
  if (record.venue_city) score += 2;
  if (record.venue_state) score += 1;
  if (record.venue_country) score += 1;
  if (record.prize_summary) score += 2;
  if (record.themes && record.themes.length) score += Math.min(2, record.themes.length * 0.5);
  if (record.end_date && record.end_date !== record.start_date) score += 1;
  return score;
}

/** Field-by-field merge: the winner's values win, and the loser backfills its nulls. */
function mergeRecords(winner, loser) {
  const merged = { ...winner };

  const backfill = [
    'registration_deadline', 'venue_city', 'venue_state', 'venue_country',
    'prize_summary', 'end_date',
  ];
  for (const field of backfill) {
    if (merged[field] == null && loser[field] != null) merged[field] = loser[field];
  }

  // Coordinates only travel as a pair — mixing lat from one source with long from
  // another would put the event in the ocean.
  if ((merged.lat == null || merged.long == null) && loser.lat != null && loser.long != null) {
    merged.lat = loser.lat;
    merged.long = loser.long;
  }

  // An in-person or hybrid signal beats a bare "online" default, since a source that
  // knows a venue is more informative than one that doesn't.
  if (merged.format === 'online' && loser.format !== 'online' && loser.venue_city) {
    merged.format = loser.format;
  }

  merged.themes = [...new Set([...(merged.themes || []), ...(loser.themes || [])])];

  const seen = new Set();
  merged.sources = [...(merged.sources || []), ...(loser.sources || [])].filter((s) => {
    if (seen.has(s.url)) return false;
    seen.add(s.url);
    return true;
  });

  return merged;
}

/**
 * Collapse duplicates across sources.
 *
 * O(n·k) where k is the number of already-accepted records sharing a name bucket —
 * we bucket by first-letter + length band so this doesn't degrade to O(n²) on
 * a few hundred records.
 */
export function dedupeHackathons(hackathons) {
  const accepted = [];
  const buckets = new Map();

  const bucketKeys = (record) => {
    // Bucket on the same key similarity is measured with, so year-suffixed variants
    // land in the same band rather than relying on the neighbour check alone.
    const normalized = matchKey(record.name).replace(/\s/g, '');
    const head = normalized.slice(0, 1) || '_';
    const band = Math.floor(normalized.length / 4);
    // Check neighbouring length bands too: "hackmit" and "hackmit2026" differ in length.
    return [`${head}:${band - 1}`, `${head}:${band}`, `${head}:${band + 1}`];
  };

  for (const raw of hackathons) {
    const record = {
      ...raw,
      sources: raw.sources || [
        { platform: raw.source_platform, url: raw.source_url },
      ],
    };

    const candidateIndexes = new Set();
    for (const key of bucketKeys(record)) {
      for (const index of buckets.get(key) || []) candidateIndexes.add(index);
    }

    let mergedInto = -1;
    for (const index of candidateIndexes) {
      if (isDuplicate(accepted[index], record)) {
        mergedInto = index;
        break;
      }
    }

    if (mergedInto >= 0) {
      const existing = accepted[mergedInto];
      const [winner, loser] =
        completenessScore(record) > completenessScore(existing)
          ? [record, existing]
          : [existing, record];
      accepted[mergedInto] = mergeRecords(winner, loser);
    } else {
      const index = accepted.length;
      accepted.push(record);
      // Index the new record under its own middle bucket only.
      const key = bucketKeys(record)[1];
      if (!buckets.has(key)) buckets.set(key, []);
      buckets.get(key).push(index);
    }
  }

  return accepted;
}
